import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Toggle, Dropdown, Divider, Icon } from '@ets/design-system';

import { useFind } from './FindContext';

import styles from './FindInstances.module.scss';

export type FindInstancesProps = {
  totalInstances: number | undefined;
  currentInstanceIndex: number;
  onChangeInstance: (newInstanceIndex: number) => void;
};

const FindInstances = ({
  currentInstanceIndex,
  totalInstances,
  onChangeInstance,
}: FindInstancesProps) => {
  const { matchCase, wholeWord, toggleMatchCase, toggleWholeWord } = useFind();
  const [isOpen, setIsOpen] = useState(false);

  const next = () => {
    if (totalInstances != null) {
      let newIndex = (currentInstanceIndex + 1) % totalInstances;
      onChangeInstance(newIndex);
    }
  };

  const previous = () => {
    if (totalInstances != null) {
      let newIndex = (currentInstanceIndex + totalInstances - 1) % totalInstances;
      onChangeInstance(newIndex);
    }
  };

  if (totalInstances == null) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={`${styles.text} ${totalInstances === 0 && styles.noMatches}`}>
        {totalInstances > 0 ? (
          <FormattedMessage
            id="NUMBER_OF_INSTANCES"
            values={{ current: currentInstanceIndex + 1, total: totalInstances }}
          />
        ) : (
          <FormattedMessage id="NO_MATCHES_FOUND" />
        )}
      </div>
      <div className={styles.toggles}>
        <Toggle
          size="m"
          variant="link"
          iconProps={{ icon: 'chevron-up' }}
          onToggle={previous}
          disabled={totalInstances === 0}
          testId="next-instance"
        />
        <Toggle
          size="m"
          variant="link"
          iconProps={{ icon: 'chevron-down' }}
          onToggle={next}
          disabled={totalInstances === 0}
          testId="previous-instance"
        />
      </div>
      <Divider margin="0.25rem 0 0.25rem 0" vertical />
      <Dropdown.Root open={isOpen} onOpenChange={setIsOpen}>
        <Dropdown.Trigger>
          <Toggle
            size="m"
            variant="link"
            iconProps={{ icon: 'ellipsis' }}
            style={{ margin: '0 1rem 0 0.75rem' }}
            isToggled={isOpen}
            disabled={totalInstances === 0}
            testId="more-options"
            onClick={() => setIsOpen(!isOpen)}
          />
        </Dropdown.Trigger>

        <Dropdown.Content align="end" testId="more-options-dropdown">
          <Dropdown.Item
            onClick={toggleMatchCase}
            testId="match-case-dropdown-item"
            suffix={matchCase ? <Icon size="2" icon="check" testId="check" /> : undefined}
          >
            <FormattedMessage id="MATCH_CASE" />
          </Dropdown.Item>
          <Dropdown.Item
            onClick={toggleWholeWord}
            testId="find-whole-word-dropdown-item"
            suffix={wholeWord ? <Icon size="2" icon="check" testId="check" /> : undefined}
          >
            <FormattedMessage id="FIND_WHOLE_WORDS" />
          </Dropdown.Item>
        </Dropdown.Content>
      </Dropdown.Root>
    </div>
  );
};

export default FindInstances;
